/* eslint-disable import/no-extraneous-dependencies */
import React, {
  createContext, useContext, useState, useCallback, useEffect, useMemo,
} from 'react'
import { jwtDecode } from 'jwt-decode'
import { signOut } from 'next-auth/react'
import Cookies from 'js-cookie'

const AuthContext = createContext(null)

const processUser = (userData) => {
  const isAuctionAdvisor = userData?.role === 'auction_advisor'
  const isPurchaseAdvisor = userData?.role === 'purchase_advisor'
  const isDealerAdmin = userData?.role === 'dealer_admin'

  return {
    ...userData,
    isKam: isAuctionAdvisor || userData?.isKam,
    isDealerAdmin,
    // dealer_admin entra por el mismo login de staff, así que comparte el flujo de logout
    isAdvisor: isAuctionAdvisor || isPurchaseAdvisor || isDealerAdmin || userData?.isKam,
  }
}

export const AuthProvider = ({ children, initialUser }) => {
  const [user, setUser] = useState(initialUser || null)
  const [loading, setLoading] = useState(false)
  const [authChecked, setAuthChecked] = useState(false)

  const clearSession = useCallback(() => {
    localStorage.removeItem('jwt')
    localStorage.removeItem('user_data')
    Cookies.remove('jwt', { path: '/' })

    const nextAuthCookies = [
      'next-auth.session-token',
      '__Secure-next-auth.session-token',
      'next-auth.callback-url',
      'next-auth.csrf-token',
      'next-auth.pkce.code_verifier',
    ]

    nextAuthCookies.forEach((name) => {
      Cookies.remove(name, { path: '/' })
      Cookies.remove(name, { path: '/', domain: window.location.hostname })
    })

    setUser(null)
  }, [])

  const logout = useCallback(async () => {
    const wasAdvisor = user?.isAdvisor
    clearSession()

    if (wasAdvisor) {
      window.location.href = '/login'
    } else {
      await signOut({
        callbackUrl: '/',
        redirect: true,
      })
    }
  }, [clearSession, user])

  useEffect(() => {
    const token = Cookies.get('jwt') || localStorage.getItem('jwt')
    const savedUser = localStorage.getItem('user_data')

    if (token && savedUser) {
      try {
        const decoded = jwtDecode(token)
        if (decoded.exp * 1000 > Date.now()) {
          setUser(JSON.parse(savedUser))
        } else {
          clearSession()
        }
      } catch (e) {
        clearSession()
      }
    }
    setAuthChecked(true)
  }, [clearSession])

  const login = useCallback(async (email, password) => {
    setLoading(true)
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_ROOT}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: { email, password } }),
      })

      if (!response.ok) throw new Error('Credenciales inválidas')

      const data = await response.json()
      const authHeader = response.headers.get('Authorization') || response.headers.get('authorization')
      const token = authHeader?.replace('Bearer ', '')
      const userData = data.user || data

      if (token && userData) {
        const userProcessed = processUser(userData)

        // Guardamos en ambos lados para máxima compatibilidad
        localStorage.setItem('jwt', token)
        localStorage.setItem('user_data', JSON.stringify(userProcessed))

        Cookies.set('jwt', token, {
          expires: 1, // 1 día de duración
          path: '/',
          secure: window.location.protocol === 'https:',
          sameSite: 'lax',
        })

        setUser(userProcessed)
        // Se devuelve el usuario para que quien llama pueda enrutar según el rol
        return { success: true, user: userProcessed }
      }
      throw new Error('Error con tus credenciales')
    } catch (error) {
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }, [])

  const value = useMemo(() => ({
    user,
    login,
    logout,
    loading,
    authChecked,
    isKam: user?.isKam,
    isAdvisor: user?.isAdvisor,
    isDealerAdmin: user?.isDealerAdmin,
  }), [user, login, logout, loading, authChecked])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
